<template>
  <div id="mapContainer" class="map flex-element"></div>
</template>

<script>
// Mapbox.
import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl";

// Local classes & config.
import config from ".././config/mapConfig.json";
import FeaturePoint from "../js/FeaturePoint";
import walks from ".././config/data.json";
import Popup from "./Popup.vue";

import iconNotFound from "../../public/img/icons/icon-not-found.png";

import iconPosPathway from "../../public/img/icons/pos-pathway.png";
import iconPosSignage from "../../public/img/icons/pos-signage.png";
import iconPosParking from "../../public/img/icons/pos-parking.png";
import iconPosOther from "../../public/img/icons/pos-other.png";
import iconPosFacilities from "../../public/img/icons/pos-facilities.png";
import iconPosEntryExit from "../../public/img/icons/pos-entry-exit.png";
import iconPosBridge from "../../public/img/icons/pos-bridge-crossing.png";
import iconPosFeature from "../../public/img/icons/pos-feature.png";
import iconPosCrossroads from "../../public/img/icons/pos-crossroads.png";

import iconNegPathway from "../../public/img/icons/neg-pathway.png";
import iconNegSignage from "../../public/img/icons/neg-signage.png";
import iconNegParking from "../../public/img/icons/neg-parking.png";
import iconNegOther from "../../public/img/icons/neg-other.png";
import iconNegFacilities from "../../public/img/icons/neg-facilities.png";
import iconNegEntryExit from "../../public/img/icons/neg-entry-exit.png";
import iconNegBridge from "../../public/img/icons/neg-bridge-crossing.png";
import iconNegFeature from "../../public/img/icons/neg-feature.png";
import iconNegCrossroads from "../../public/img/icons/neg-crossroads.png";

export default {
  name: "MapView",

  props: {
    selectedWalkId: null,
    zoomToSelectedWalk: null,
    selectWalk: null,
  },

  components: {
    /* eslint-disable */
    Popup,
    /* eslint-enable */
  },

  watch: {
    zoomToSelectedWalk() {
      this.zoomToWalk(this.selectedWalkId, this.selectWalk);
    },
  },

  data() {
    return {
      mbMap: null,
      dataLayer: null,
      jsonPoints: {},
      walks: walks,
      currentlySelectedWalk: null,
    };
  },

  async mounted() {
    mapboxgl.accessToken = config.accessToken;
    this.mbMap = new mapboxgl.Map(config.mapConfig);
    let context = this;

    let icons = [
      iconNotFound,
      iconPosPathway,
      iconPosSignage,
      iconPosParking,
      iconPosOther,
      iconPosFacilities,
      iconPosEntryExit,
      iconPosBridge,
      iconPosFeature,
      iconPosCrossroads,
      iconNegPathway,
      iconNegSignage,
      iconNegParking,
      iconNegOther,
      iconNegFacilities,
      iconNegEntryExit,
      iconNegBridge,
      iconNegFeature,
      iconNegCrossroads,
    ];

    // Mapbox Async local icon loading.
    icons.forEach(async function (icon) {
      await context.mbMap.loadImage(icon, function (error, image) {
        let iconName = icon.split("img/")[1].split(".")[0];
        context.mbMap.addImage(iconName, image);
      });
    });

    this.mbMap.on("load", () => {
      this.$emit("mapLoaded");
      // Add shared data.
      this.jsonPoints = this.parseDataToGeoJson(this.walks);
      context.mbMap.addSource("dataLayer", {
        type: "geojson",
        data: this.jsonPoints,
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 60,
      });

      // Aggregated points (clusters) with labels.
      context.mbMap.addLayer(config.layerConfig.clusteredPointsLayer);
      context.mbMap.addLayer(config.layerConfig.clusterCountLabelsLayer);

      // Add new point & label layers for all walks + mouse cursors & on-click popups.
      this.walks.forEach((walk) => {
        let pointsLayer = config.layerConfig.pointsLayerSymbols;
        pointsLayer.id = String(walk.ID);
        // pointsLayer.filter = ["==", "walkId", walk.ID]
        pointsLayer.filter = [
          "all",
          ["==", "walkId", walk.ID],
          ["!has", "point_count"],
        ];
        context.mbMap.addLayer(pointsLayer);

        // Hover on mouse-over.
        context.mbMap.on("mouseenter", pointsLayer.id, function () {
          context.mbMap.getCanvas().style.cursor = "pointer";
        });

        // Popup functionality.
        context.mbMap.on("click", pointsLayer.id, this.handleMapClick);
      });
    });
  },

  methods: {
    parseDataToGeoJson: function (walks) {
      // Iterate all walks, assign the walk's ID to all of its points, and then return a flattened list of those points.
      let points = walks
        .map((walk) => {
          return walk.Points.map((point) => {
            point.walkId = walk.ID;
            return point;
          });
        })
        .flat();
      let geoJsonPoints = [];
      points.forEach((point) => {
        geoJsonPoints.push(new FeaturePoint(point));
      });
      return {
        type: "FeatureCollection",
        features: geoJsonPoints.map((feature) => feature.featureJson),
      };
    },

    zoomToWalk: function (id, selectWalk) {
      // Set the
      this.currentlySelectedWalk = this.currentlySelectedWalk ?? null;

      // Fly to the central points of the appropriate walk.
      if (selectWalk) {
        let walk = this.walks.filter((walk) => walk.ID === id)[0];
        let middlePoint = walk.Points[parseInt(walk.Points.length / 2)];
        this.mbMap.flyTo({
          center: [middlePoint.Lng, middlePoint.Lat],
          zoom: 14,
        });

        // Hide all walks.
        walks.forEach((walk) => {
          this.mbMap.setLayoutProperty(walk.ID, "visibility", "none");
        });
        // Display the selected layer.
        this.mbMap.setLayoutProperty(id, "visibility", "visible");
      } else if (!selectWalk) {
        // Make all walks visible.
        walks.forEach((walk) => {
          this.mbMap.setLayoutProperty(walk.ID, "visibility", "visible");
        });
      }
    },

    handleMapClick: function (e) {
      // Create a new popup object.
      let point = e.features[0];
      window.popup = new mapboxgl.Popup({ className: "customPopupTemplate" })
        .setLngLat(point.geometry.coordinates.slice())
        .setHTML('<div id="vue-popup-content"></div>')
        .addTo(this.mbMap);

      // Mount our custom popup.
      new Popup({
        propsData: {
          popupImage: e.features[0].properties.imageLink,
          modalImage: e.features[0].properties.modalImage,
          description: e.features[0].properties.description,
        },
      }).$mount("#vue-popup-content");
    },
  },
};
</script>
